'use client';

import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

export default function TermsPage() {
  return (
    <div className="flex flex-col min-h-screen">
      {/* Header */}
      <section className="bg-primary/10 py-12">
        <div className="container mx-auto px-4">
          <Link href="/">
            <Button variant="ghost" className="mb-8 gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Button>
          </Link>
          <h1 className="text-4xl font-bold mb-4">Terms and Conditions</h1>
          <p className="text-xl text-muted-foreground max-w-3xl">
            Please read these terms carefully before using Kawkb.
          </p>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-bold mb-4">1. Introduction</h2>
            <p>
              Welcome to Kawkb! These Terms and Conditions ("Terms") govern your use of our
              platform, including our website (the "Site"), mobile applications (the "Apps"), and
              any related services (collectively, the "Services"). By accessing or using Kawkb, you
              agree to comply with these Terms. If you do not agree, please discontinue use.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">2. Eligibility</h2>
            <p>
              To use Kawkb, you must be at least 18 years old and legally capable of entering into
              binding agreements. By registering, you confirm that the information you provide is
              accurate and up to date.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">3. User Accounts</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                You are responsible for maintaining the confidentiality of your account credentials.
              </li>
              <li>
                You agree not to share your login details or allow others to use your account.
              </li>
              <li>You must promptly notify us of any unauthorized use of your account.</li>
            </ul>

            <h2 className="text-2xl font-bold mb-4 mt-8">4. Hosting on Kawkb</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Hosts must ensure that listed properties are furnished and located within gated
                communities, as this aligns with our target audience.
              </li>
              <li>
                Hosts are responsible for compliance with local regulations, including registration
                and taxation.
              </li>
              <li>Listings must provide accurate descriptions and photos of the property.</li>
              <li>
                Hosts must adhere to safety standards and maintain their properties in good
                condition.
              </li>
            </ul>

            <h2 className="text-2xl font-bold mb-4 mt-8">5. Booking and Payments</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Guests must adhere to the house rules set by the host during their stay.</li>
              <li>
                Payments are processed securely through our platform. Hosts will receive payouts
                within 48 business hours after guest check-in.
              </li>
              <li>
                Cancellation and refund policies are outlined on the booking page. Make sure to
                review them before confirming a booking.
              </li>
            </ul>

            <h2 className="text-2xl font-bold mb-4 mt-8">6. Code of Conduct</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Users must act respectfully towards others within the Kawkb community.</li>
              <li>Discrimination, harassment, or any illegal activity is strictly prohibited.</li>
              <li>Violations may result in suspension or termination of your account.</li>
            </ul>

            <h2 className="text-2xl font-bold mb-4 mt-8">
              7. Guest Responsibilities and Liability
            </h2>
            <h3 className="text-xl font-semibold mb-2">A. Use of the Accommodation</h3>
            <p>
              Accommodations are for personal use only and must not be sublet or used for commercial
              purposes. Guests must comply with all house rules, which will be communicated before
              or during check-in.
            </p>

            <h3 className="text-xl font-semibold mb-2 mt-4">B. Damages</h3>
            <p>
              Guests are responsible for maintaining the accommodation in its original condition.
              Any damage to the property or furnishings will result in charges for repair or
              replacement. Guests will receive an itemized breakdown of damage charges.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">8. Liability and Disclaimer</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Your use of the Services and Accommodations is at your sole risk. Kawkb disclaims
                all warranties, including fitness for a particular purpose and non-infringement.
              </li>
              <li>
                Kawkb solely facilitates the booking and hosting process between users. We do not
                own, manage, or control the properties listed on our platform.
              </li>
              <li>
                Kawkb is not liable for any damages, losses, or disputes that may arise between
                hosts and guests, including but not limited to property damage, personal injury, or
                legal violations.
              </li>
              <li>
                In case of damage or violations, the Protection Plan may cover eligible cases as
                outlined in the plan details, but the responsibility remains with the host and guest
                involved.
              </li>
              <li>
                By using Kawkb, you acknowledge that we are not a party to rental agreements or
                transactions between hosts and guests, and our role is limited to providing the
                platform for such interactions.
              </li>
            </ul>

            <h2 className="text-2xl font-bold mb-4 mt-8">9. Intellectual Property</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Content uploaded by users (such as property photos and descriptions) remains the
                property of the user but grants Kawkb a license to use and display it on the
                platform.
              </li>
              <li>
                All trademarks, logos, and service marks on Kawkb are the property of their
                respective owners.
              </li>
            </ul>

            <h2 className="text-2xl font-bold mb-4 mt-8">10. Privacy and Data Protection</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Your use of Kawkb is subject to our Privacy Policy, which outlines how we collect,
                use, and protect your data.
              </li>
              <li>
                By using the platform, you consent to data practices as described in the Privacy
                Policy.
              </li>
            </ul>

            <h2 className="text-2xl font-bold mb-4 mt-8">11. Termination and Suspension</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Kawkb reserves the right to suspend or terminate accounts that violate these Terms
                or misuse the platform.
              </li>
              <li>You may close your account at any time by contacting support.</li>
            </ul>

            <h2 className="text-2xl font-bold mb-4 mt-8">12. Changes to the Terms</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                We may update these Terms periodically to reflect changes in our services or legal
                requirements.
              </li>
              <li>
                Continued use of Kawkb after changes are posted constitutes acceptance of the
                revised Terms.
              </li>
            </ul>

            <h2 className="text-2xl font-bold mb-4 mt-8">13. Governing Law</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>These Terms are governed by the laws of the Arab Republic of Egypt.</li>
              <li>
                Any disputes arising from these Terms will be resolved exclusively in the courts of
                Cairo, Egypt.
              </li>
            </ul>

            <h2 className="text-2xl font-bold mb-4 mt-8">14. Contact Us</h2>
            <p>For any questions or concerns regarding these Terms, contact us at:</p>
            <ul className="list-none pl-6 space-y-1 mt-2">
              <li>
                <strong>Email:</strong>{' '}
                <a href="mailto:support@kawkb.com" className="text-blue-600 hover:underline">
                  support@kawkb.com
                </a>
              </li>
              <li>
                <strong>Phone:</strong>{' '}
                <a href="tel:+201021514433" className="text-blue-600 hover:underline">
                  +201021514433
                </a>
              </li>
            </ul>

            <div className="mt-12 pt-8 border-t border-border">
              <p className="text-muted-foreground text-sm">
                Last updated:{' '}
                {new Date().toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
